import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../apiClient";
import { Link } from "react-router-dom";
import { formatMoney } from "../formatMoney";

const Loans = () => {
  const [loans, setLoans] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "LoanDate", direction: "desc" });

  const filteredLoans = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = !query
      ? loans
      : loans.filter((loan) =>
          [loan.Description, loan.LenderName, loan.LoanDate, loan.LatestBalanceDate]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query))
        );

    return [...filtered].sort((left, right) => {
      const leftValue = left[sortConfig.key];
      const rightValue = right[sortConfig.key];
      const leftNumeric = Number(leftValue);
      const rightNumeric = Number(rightValue);
      const comparison =
        Number.isFinite(leftNumeric) && Number.isFinite(rightNumeric)
          ? leftNumeric - rightNumeric
          : String(leftValue || "").localeCompare(String(rightValue || ""));
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [loans, search, sortConfig]);

  const totalLiabilities = useMemo(
    () => loans.reduce((sum, loan) => sum + Number(loan.LatestBalanceAmount || 0), 0),
    [loans]
  );

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        const [loansRes, currenciesRes] = await Promise.all([
          apiClient.get("/loans"),
          apiClient.get("/currencies"),
        ]);
        setLoans(Array.isArray(loansRes.data) ? loansRes.data : []);
        const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load loans.");
      } finally {
        setLoading(false);
      }
    };

    fetchLoans();
  }, []);

  const handleSort = (key) => {
    setSortConfig((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === "LoanDate" ? "desc" : "asc" }
    );
  };

  const handleDelete = async (loan) => {
    if (!window.confirm(`Are you sure you wish to delete ${loan.Description} loan?`)) return;

    try {
      await apiClient.delete(`/loans/${loan.LoanID}`);
      setLoans((current) => current.filter((row) => row.LoanID !== loan.LoanID));
    } catch (err) {
      setError(err?.response?.data?.message || "Delete failed. Please try again.");
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Loans</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / Loans
          </p>
        </div>
        <div className="actions">
          <Link className="btn-link" to="/LoanBalances">Enter Balances</Link>
          <Link className="btn-link" to="/LoansAdd">Add Loan</Link>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading loans...</p>}

      {!loading && (
        <section className="resource-list">
          {loans.length === 0 ? (
            <article className="panel empty-state">
              <h2>No loans yet</h2>
              <p className="notice notice-muted">Add a loan before recording dated liability balances.</p>
              <div className="form-actions">
                <Link className="btn" to="/LoansAdd">Add First Loan</Link>
              </div>
            </article>
          ) : (
            <>
              <article className="panel">
                <h2>Total Liabilities</h2>
                <div className="dashboard-grid">
                  <div className="metric-card metric-card-compact">
                    <strong>{defaultCurrency || "Default"}</strong>
                    <div className="resource-meta">
                      {formatMoney(totalLiabilities, defaultCurrency || "Default")}
                    </div>
                  </div>
                </div>
              </article>

              <section className="panel page-cta list-filter-panel">
                <div className="list-filters">
                  <label className="list-filter-field">
                    <span>Search</span>
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Find a loan"
                    />
                  </label>
                </div>
              </section>

              <div className="table-scroll">
                <table className="resource-table">
                  <thead>
                    <tr>
                      <th><button type="button" className="table-sort" onClick={() => handleSort("Description")}>Loan</button></th>
                      <th><button type="button" className="table-sort" onClick={() => handleSort("LenderName")}>Lender</button></th>
                      <th><button type="button" className="table-sort" onClick={() => handleSort("LoanDate")}>Loan Date</button></th>
                      <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("LatestBalanceAmount")}>Latest Balance</button></th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLoans.map((loan) => (
                      <tr key={loan.LoanID}>
                        <td data-label="Loan"><strong>{loan.Description}</strong></td>
                        <td data-label="Lender">{loan.LenderName || "-"}</td>
                        <td data-label="Loan Date">{loan.LoanDate || "-"}</td>
                        <td data-label="Latest Balance" className="table-number table-money">
                          {loan.LatestBalanceAmount == null
                            ? "-"
                            : formatMoney(loan.LatestBalanceAmount, defaultCurrency || "Default")}
                          {loan.LatestBalanceDate ? (
                            <div className="resource-meta">{loan.LatestBalanceDate}</div>
                          ) : null}
                        </td>
                        <td data-label="Actions" className="resource-table-actions">
                          <Link className="btn-link" to={`/LoansUpdate/${loan.LoanID}`}>Edit</Link>
                          <Link className="action-link" to={`/LoanBalances?loanId=${loan.LoanID}`}>Balances</Link>
                          <button className="action-link action-link-danger" type="button" onClick={() => handleDelete(loan)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default Loans;
